import { useMemo, useState, useEffect, useRef } from "react";
import { gql } from "@apollo/client";
import { apolloClient } from "@/lib/apollo/client";
import { ME } from "@/graphql/queries/users";
import { GET_REQUEST_TO_JOIN } from "@/graphql/queries/events";

import AppModal from "@/components/ui/AppModal";
import MultiSelect, { MultiSelectOption } from "@/components/ui/MultiSelect";
import NewFamilyMemberModal from "./NewFamilyMemberModal";
import {
  PERSONAL_PROFILE_KEYWORDS,
  mapEventTypeToSport,
} from "@/features/user/utilData";
import { useEventsFeature } from "@/features/events/useEventsFeature";
import { useFamilyMembersInUser } from "@/graphql/hooks/useUsers";
import { useUpdateUserProfile } from "@/graphql/hooks/useAdmin";
import { useToast } from "@/components/ui/ToastProvider";
import {
  useGetAllKeywordCategories,
  useRequestEventAttendance,
  useRequestToJoin,
} from "@/graphql/hooks/useEvents";
import { useAppSelector } from "@/store/hooks";

const inputCls =
  "w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none transition-colors focus:border-[var(--sidebar-bg)] focus:ring-2 focus:ring-[var(--sidebar-bg)]/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-50";

function FormField({ label, children, required }: any) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div className="mb-2 mt-5 first:mt-0">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
        {title}
      </h3>
      <div className="mt-1 h-px bg-blue-500 dark:bg-blue-600" />
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string | null;
  currentUser?: any;
  initialSubmittedFor?: "Myself" | "Family Member";
  existingAttendeeUserIds?: string[];
  onSuccess?: () => void;
}

// const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const GENDER = ["Male", "Female",  "Other"];

export default function RequestModal({
  isOpen,
  onClose,
  currentUser,
  initialSubmittedFor,
  existingAttendeeUserIds = [],
  onSuccess,
}: Props) {
  const lastLoadedUserRef = useRef<string | null>(null);
  const [isNewFamilyOpen, setIsNewFamilyOpen] = useState(false);
  const [additionalFamilyMembers, setAdditionalFamilyMembers] = useState<{
    id: string;
    label: string;
  }[]>([]);
  const attendeeStatus =
    useAppSelector((s) => s.user.attendeeStatus) ?? currentUser?.attendeeStatus;
  console.info("RequestModal initialSubmittedFor", initialSubmittedFor);

  // current event context and inferred sport mapping
  const { currentEvent } = useEventsFeature();
  const inferredSport = mapEventTypeToSport(
    (currentEvent && (currentEvent.platformEventType || currentEvent.eventType || currentEvent.type)) || "",
  );

  // Determine if SPORTS keywords should be enforced in the UI for the requesting user
  const isSportsEvent = Boolean(inferredSport);
  const requesterCompanyId =
    currentUser?.selectedCompanyMembership?.companyId ||
    currentUser?.selectedCompanyMembership?.id ||
    null;
  const eventCompanyId = currentEvent?.organiser?.company?.id || null;
  const isRequesterCompany = Boolean(requesterCompanyId && eventCompanyId && requesterCompanyId === eventCompanyId);

  const [form, setForm] = useState({
    submittedFor: "Myself" as "Myself" | "Family Member",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    team: "",
    notes: "",
    ethnicity: [] as string[],
    citizenship: [] as string[],
    medical: [] as string[],
    allergies: [] as string[],
    height: [] as string[],
    gender: [] as string[],
    address: "",
    city: "",
    postalCode: "",
    country: "",
    familyMember: [] as string[],
    sportProfile: {
      sport: "",
      sportId: "",
      currentClub: "",
      preferredPosition: [] as string[],
      secondaryPosition: [] as string[],
      dominantFoot: [] as string[],
      dominantHand: [] as string[],
      trainingFrequency: [] as string[],
      preferredTrainingStyle: [] as string[],
      pastInjuries: [] as string[],
      sportPic: "",
      trialVideo: "",
      isActive: false,
      otherClubs: [] as string[],
      preferred_positions_short: [] as string[],
      protective_gear_used: [] as string[],
      secondary_positions_short: [] as string[],
      supplements_used: [] as string[],
      walking_frequency_over_30mins: [] as string[],
    },
    termsAccepted: false,
    videoConsent: false,
    socialMediaConsent: false,
  });

  const clearPersonalFormFields = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    team: "",
    notes: "",
    ethnicity: [] as string[],
    citizenship: [] as string[],
    medical: [] as string[],
    allergies: [] as string[],
    height: [] as string[],
    gender: [] as string[],
    address: "",
    city: "",
    postalCode: "",
    country: "",
    familyMember: [] as string[],
    sportProfile: {
      sport: "",
      sportId: "",
      currentClub: "",
      preferredPosition: [] as string[],
      secondaryPosition: [] as string[],
      dominantFoot: [] as string[],
      dominantHand: [] as string[],
      trainingFrequency: [] as string[],
      preferredTrainingStyle: [] as string[],
      pastInjuries: [] as string[],
      sportPic: "",
      trialVideo: "",
      isActive: false,
      otherClubs: [] as string[],
      preferred_positions_short: [] as string[],
      protective_gear_used: [] as string[],
      secondary_positions_short: [] as string[],
      supplements_used: [] as string[],
      walking_frequency_over_30mins: [] as string[],
    },
    termsAccepted: false,
    videoConsent: false,
    socialMediaConsent: false,
  });

  const handleSubmittedForChange = (opt: "Myself" | "Family Member") => {
    if (opt === "Family Member") {
      // Clear personal inputs and preview state when switching to family member
      setForm((f: any) => ({ ...f, submittedFor: opt, ...clearPersonalFormFields() }));
      setSelectedCategorisedKeywords(null);
      setSelectedProfileBio(null);
      setSelectedProfiles([]);
      // reset lastLoadedUserRef so selecting family members triggers fetch
      try {
        lastLoadedUserRef.current = null;
      } catch (e) {
        /* ignore */
      }
      return;
    }

    // Switching back to Myself: allow existing effect to reload currentUser values
    try {
      lastLoadedUserRef.current = null;
    } catch (e) {
      /* ignore */
    }
    setForm((f: any) => ({ ...f, submittedFor: opt }));
  };

  const { data: familyData = [], loading: familyLoading } =
    useFamilyMembersInUser(currentUser?.id ?? null, !isOpen);

  const {
    joinState,
    loading: joinStateLoading,
    error: joinStateError,
  } = useRequestToJoin(currentEvent?.id ?? "", isOpen && Boolean(currentUser?.id));
  const awaitingRequesterIds = useMemo(
    () => joinState?.requests
      .filter((request) => request.status.toUpperCase() === "AWAITING")
      .map((request) => request.requesterId) ?? [],
    [joinState],
  );
  // Request all keyword categories (don't force a specific keywordType)
  const { keywordCategories } = useGetAllKeywordCategories(undefined, undefined, undefined, !isOpen);
  const { updateUserProfile } = useUpdateUserProfile();
  const { requestEventAttendance } = useRequestEventAttendance();
  const { addToast } = useToast();
  const [apiMessage, setApiMessage] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);
  const [selectedCategorisedKeywords, setSelectedCategorisedKeywords] = useState<any | null>(null);
  const [selectedProfileBio, setSelectedProfileBio] = useState<string | null>(null);
  const [selectedProfiles, setSelectedProfiles] = useState<any[] | null>(null);
  const [selectedLocale, setSelectedLocale] = useState<string>("en");

  const extractKeyword = (compositeId?: string | string[]): string => {
    if (!compositeId) return "";
    const id = Array.isArray(compositeId) ? compositeId[0] : compositeId;
    if (typeof id !== "string") return "";
    const parts = id.split("_");
    return parts.length > 1 && parts[0] ? parts[0] : id;
  };

  const formatDateToInput = (dateString?: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  const normalizeGender = (gender?: string): string => {
    if (!gender) return "";
    return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
  };

  const getKeywordCategoryBySlug = (slug: string) => {
    if (!Array.isArray(keywordCategories)) return null;
    return keywordCategories.find((cat: any) => cat.slug === slug) || null;
  };

  const buildKeywordOptions = (
    categorySlug: string,
  ): { value: string; label: string }[] => {
    const category = getKeywordCategoryBySlug(categorySlug);
    if (!category || !Array.isArray(category.keywords)) return [];
    return category.keywords.map((kw: any) => ({
      value: kw.slug,
      label: kw.displayName || kw.slug,
    }));
  };

  const buildPositionOptions = (
    categorySlug: string,
    sport?: string,
  ): { value: string; label: string }[] => {
    const category = getKeywordCategoryBySlug(categorySlug);
    if (!category || !Array.isArray(category.keywords)) return [];

    const targetSport = sport || form.sportProfile.sport;
    if (!targetSport) return [];

    return category.keywords
      .filter((kw: any) => kw.slug && kw.slug.includes("soccer"))
      .map((kw: any) => {
        const raw = kw.displayName || kw.slug || "";
        const label = String(raw).replace(/^.*? -\s*/, "");
        return { value: kw.slug, label };
      });
  };

  const findMatchingApiSlug = (
    compositeId?: string | string[],
    categorySlug?: string,
  ): string => {
    if (!compositeId || !categorySlug) return "";

    const id = Array.isArray(compositeId) ? compositeId[0] : compositeId;
    if (typeof id !== "string") return "";

    const category = getKeywordCategoryBySlug(categorySlug);
    if (!category || !Array.isArray(category.keywords)) return "";

    const keyword = category.keywords.find((kw: any) => {
      if (kw.slug === id) return true;
      if (kw.slug && kw.slug.startsWith(id)) return true;
      const extractedKeyword = extractKeyword(id).toLowerCase();
      if (!extractedKeyword) return false;
      return kw.slug.toLowerCase().includes(extractedKeyword);
    });

    return keyword?.slug || "";
  };

  type CategorisedResult = {
    ethnicity: string[];
    height_range: string[];
    citizenship_multi_select: string[];
    chronic_conditions: string[];
    allergies: string[];
    PERSONAL: string[];
  };

  const parseCategorisedKeywords = (categorisedKeywords?: any[]) => {
    const result: CategorisedResult = {
      ethnicity: [],
      height_range: [],
      citizenship_multi_select: [],
      chronic_conditions: [],
      allergies: [],
      PERSONAL: [],
    };
    if (!Array.isArray(categorisedKeywords)) return result;

    // first pick up explicit typed entries
    categorisedKeywords.forEach((c: any) => {
      const t = String(c?.type || "");
      if (!Array.isArray(c?.keywords)) return;
      if (!t) return;
      // explicitly handle known keys so TypeScript can verify them
      switch (t) {
        case "ethnicity":
          result.ethnicity = Array.from(new Set([...(result.ethnicity || []), ...c.keywords]));
          break;
        case "height_range":
          result.height_range = Array.from(new Set([...(result.height_range || []), ...c.keywords]));
          break;
        case "citizenship_multi_select":
          result.citizenship_multi_select = Array.from(new Set([...(result.citizenship_multi_select || []), ...c.keywords]));
          break;
        case "chronic_conditions":
          result.chronic_conditions = Array.from(new Set([...(result.chronic_conditions || []), ...c.keywords]));
          break;
        case "allergies":
          result.allergies = Array.from(new Set([...(result.allergies || []), ...c.keywords]));
          break;
        case "PERSONAL":
          result.PERSONAL = Array.from(new Set([...(result.PERSONAL || []), ...c.keywords]));
          break;
        default:
          break;
      }
    });

    // if specific typed entries are missing, try to extract from PERSONAL bucket
    const personal = categorisedKeywords.find((c: any) => String(c?.type) === "PERSONAL");
    if (personal) {
      // PERSONAL may be provided as { keywords: ["a","b"] } (legacy) or
      // as a mapping object { ethnicity: [...], allergies: [...] }. Handle both.
      if (Array.isArray(personal.keywords)) {
        const personalKeywords: string[] = personal.keywords;
        personalKeywords.forEach((kw) => {
          if (!kw || typeof kw !== "string") return;
          const lower = kw.toLowerCase();

          if (lower.includes("_cm") || lower.includes("cm_") || /\d+_?cm/.test(lower)) {
            if (!result.height_range.includes(kw)) result.height_range.push(kw);
            return;
          }

          if (/(asthma|diabetes|epilepsy|cancer|heart|hypertension)/.test(lower)) {
            if (!result.chronic_conditions.includes(kw)) result.chronic_conditions.push(kw);
            return;
          }

          if (/(soy|peanut|peanuts|dairy|milk|egg|gluten|nuts|shellfish|fish)/.test(lower)) {
            if (!result.allergies.includes(kw)) result.allergies.push(kw);
            return;
          }

          if (/(asian|indian|hindu|african|european|latino|hispanic|arab|pakistani|bangladeshi)/.test(lower)) {
            if (!result.ethnicity.includes(kw)) result.ethnicity.push(kw);
            return;
          }

          if (/^[a-z]+_[0-9a-f\-]{8,}/.test(lower) || /^(american|algerian|british|australian|canadian|irish|indian|pakistani)/.test(lower)) {
            if (!result.citizenship_multi_select.includes(kw)) result.citizenship_multi_select.push(kw);
            return;
          }

          if (!result.PERSONAL.includes(kw)) result.PERSONAL.push(kw);
        });
      } else if (personal && typeof personal === "object") {
        // If PERSONAL is an object mapping, copy known fields over
        const keys = Object.keys(personal).filter((k) => k !== "type");
        keys.forEach((k) => {
          const val = personal[k];
          if (!val) return;
          if (k === "ethnicity" && Array.isArray(val)) {
            result.ethnicity = Array.from(new Set([...(result.ethnicity || []), ...val]));
          } else if (k === "height_range" && Array.isArray(val)) {
            result.height_range = Array.from(new Set([...(result.height_range || []), ...val]));
          } else if (k === "citizenship_multi_select" && Array.isArray(val)) {
            result.citizenship_multi_select = Array.from(new Set([...(result.citizenship_multi_select || []), ...val]));
          } else if (k === "chronic_conditions" && Array.isArray(val)) {
            result.chronic_conditions = Array.from(new Set([...(result.chronic_conditions || []), ...val]));
          } else if (k === "allergies" && Array.isArray(val)) {
            result.allergies = Array.from(new Set([...(result.allergies || []), ...val]));
          } else if (Array.isArray(val)) {
            // unknown array keys, push into PERSONAL bucket
            val.forEach((item: any) => {
              if (typeof item === "string" && !result.PERSONAL.includes(item)) result.PERSONAL.push(item);
            });
          }
        });
      }
    }

    return result;
  };

  const convertCategorisedKeywordsInput = (input: any): any[] => {
    if (!input) return [];
    // already in legacy array shape
    if (Array.isArray(input)) {
      return input.map((item: any) => ({
        type: item?.type ?? "",
        keywords: Array.isArray(item?.keywords) ? item.keywords.map(String) : [],
      })).filter((b: any) => b.type);
    }

    // new nested mapping shape: { TYPE: { category: [items...] } } or { TYPE: [items...] }
    if (typeof input === "object") {
      const out: any[] = [];
      for (const [type, value] of Object.entries(input)) {
        // If this is the PERSONAL mapping, expand its subkeys into separate typed entries
        if (type === "PERSONAL" && value && typeof value === "object" && !Array.isArray(value)) {
          for (const [subKey, subVal] of Object.entries(value as Record<string, any>)) {
            let subKeywords: string[] = [];
            if (Array.isArray(subVal)) {
              subKeywords = subVal
                .map((v: any) => {
                  if (v == null) return "";
                  if (typeof v === "string") return v;
                  if (typeof v === "object") return v.slug || v.id || v.value || v.label || String(v) || "";
                  return String(v);
                })
                .filter(Boolean);
            } else if (typeof subVal === "string") {
              subKeywords = [subVal];
            }
            if (subKeywords.length) out.push({ type: subKey, keywords: Array.from(new Set(subKeywords)) });
          }
          continue;
        }

        let keywords: string[] = [];

        if (Array.isArray(value)) {
          keywords = value
            .map((v: any) => {
              if (v == null) return "";
              if (typeof v === "string") return v;
              if (typeof v === "object") return v.slug || v.id || v.value || v.label || String(v) || "";
              return String(v);
            })
            .filter(Boolean);
        } else if (value && typeof value === "object") {
          // value is a mapping of subcategories -> arrays or values
          for (const sub of Object.values(value)) {
            if (Array.isArray(sub)) {
              sub.forEach((item: any) => {
                if (!item) return;
                if (typeof item === "string") keywords.push(item);
                else if (typeof item === "object") keywords.push(item.slug || item.id || item.value || item.label || String(item));
                else keywords.push(String(item));
              });
            } else if (typeof sub === "string") {
              keywords.push(sub);
            }
          }
        } else if (typeof value === "string") {
          keywords = [value];
        }

        out.push({ type, keywords: Array.from(new Set(keywords)) });
      }
      return out;
    }

    return [];
  };

  // Build a mapping-shaped categorisedKeywords object suitable for preview/UI.
  // Ensures PERSONAL contains nested subkeys by moving top-level personal fields
  // into `PERSONAL` when present.
  const buildCategorisedMappingForPreview = (input: any): Record<string, any> => {
    // If input is already a mapping object, shallow clone it
    let mapping: Record<string, any> = {};
    if (!input) return mapping;
    if (!Array.isArray(input) && typeof input === "object") {
      mapping = { ...(input as Record<string, any>) };
    } else if (Array.isArray(input)) {
      // turn array [{type, keywords, ...}] into mapping
      for (const item of input) {
        if (!item || !item.type) continue;
        const t = String(item.type);
        if (!mapping[t]) mapping[t] = {};
        // prefer typed keywords arrays placed under a 'keywords' key when present
        if (Array.isArray(item.keywords) && item.keywords.length) mapping[t].keywords = Array.from(new Set([...(mapping[t].keywords || []), ...item.keywords]));
        // copy any other subkeys
        Object.keys(item).forEach((k) => {
          if (k === "type" || k === "keywords") return;
          mapping[t][k] = item[k];
        });
      }
    }

    // Known personal keys that should be nested under PERSONAL
    const personalKeys = new Set([
      "ethnicity",
      "height_range",
      "citizenship_multi_select",
      "chronic_conditions",
      "allergies",
      "dietary_preferences",
      "highest_education",
      "motivators",
      "preferred_colour",
      "preferred_food",
      "weight_range",
    ]);

    // Ensure PERSONAL exists
    if (!mapping["PERSONAL"] || typeof mapping["PERSONAL"] !== "object") mapping["PERSONAL"] = { ...(mapping["PERSONAL"] || {}) };

    // Move any personalKeys found at top-level into PERSONAL[subkey]
    for (const key of Object.keys(mapping)) {
      if (key === "PERSONAL") continue;
      if (personalKeys.has(key)) {
        const val = mapping[key];
        // If the top-level value is an object with keywords, extract arrays
        if (val && typeof val === "object") {
          // if val.keywords exists, use that
          if (Array.isArray(val.keywords)) mapping["PERSONAL"][key] = Array.from(new Set([...(mapping["PERSONAL"][key] || []), ...val.keywords]));
          else {
            // otherwise if val is mapping, try to flatten arrays inside
            const arr: any[] = [];
            for (const sub of Object.values(val)) {
              if (Array.isArray(sub)) arr.push(...sub);
              else if (typeof sub === "string") arr.push(sub);
            }
            if (arr.length) mapping["PERSONAL"][key] = Array.from(new Set([...(mapping["PERSONAL"][key] || []), ...arr]));
          }
        } else if (Array.isArray(val)) {
          mapping["PERSONAL"][key] = Array.from(new Set([...(mapping["PERSONAL"][key] || []), ...val]));
        } else if (typeof val === "string") {
          mapping["PERSONAL"][key] = Array.from(new Set([...(mapping["PERSONAL"][key] || []), val]));
        }
        // remove the top-level key
        delete mapping[key];
      }
    }

    return mapping;
  };

  const pickProfileByLocale = (profiles: any[] | undefined | null, locale = "en") => {
    if (!Array.isArray(profiles) || profiles.length === 0) return profiles?.[0] ?? null;
    const matched = profiles.find((p: any) => String(p?.locale) === String(locale));
    return matched || profiles[0] || null;
  };

  const pickSportProfile = (profiles: any[] | undefined | null, targetSport?: string) => {
    if (!Array.isArray(profiles) || profiles.length === 0) return profiles?.[0] ?? {};
    if (targetSport) {
      const exact = profiles.find((p: any) => String(p?.sport) === String(targetSport));
      if (exact) return exact;
      const includes = profiles.find((p: any) => String(p?.sport).includes(String(targetSport)));
      if (includes) return includes;
    }
    return profiles[0];
  };

  const normalize = (s: string) =>
    String(s || "")
      .toLowerCase()
      .replace(/[’'`]/g, "")
      .replace(/[^a-z0-9]+/g, "")
      .trim();

  const humanizeSport = (slug?: string) => {
    if (!slug) return "";
    const map: Record<string, string> = {
      football_or_soccer: "Football (Soccer)",
      basketball: "Basketball",
      nfl: "American Football",
      baseball: "Baseball",
      ice_hockey: "Ice Hockey",
      tennis: "Tennis",
      rugby: "Rugby",
      cricket: "Cricket",
      volleyball: "Volleyball",
    };
    if (map[slug]) return map[slug];
    try {
      return String(slug)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    } catch {
      return String(slug);
    }
  };

  const buildServerKeywordIndex = (existingCategorised: any[] = []) => {
    const flat: string[] = [];
    (existingCategorised || []).forEach((c: any) => {
      if (Array.isArray(c?.keywords)) flat.push(...c.keywords.map(String));
    });
    return flat;
  };

  const mapServerKeywordsToFormValues = (typeKey: string, serverKeywords: string[] = [], existingCategorised: any[] = []) => {
    const options = (PERSONAL_PROFILE_KEYWORDS as any)[typeKey] ?? [];
    const idx = buildServerKeywordIndex(existingCategorised);
    const mapped: string[] = [];

    serverKeywords.forEach((kw) => {
      if (!kw || typeof kw !== "string") return;
      const nkw = normalize(kw);
      // try to find an option whose normalized string appears in the server keyword
      let found = options.find((opt: string) => nkw.includes(normalize(opt)) || normalize(opt).includes(nkw));
      if (!found) {
        // fallback: try to match against any known server keyword labels by normalization
        const match = idx.find((sk) => normalize(sk).includes(nkw) || nkw.includes(normalize(sk)));
        if (match) {
          // try to find option that matches match
          found = options.find((opt: string) => normalize(match).includes(normalize(opt)) || normalize(opt).includes(normalize(match)));
        }
      }
      mapped.push(found || kw);
    });

    return Array.from(new Set(mapped));
  };

  const mapFormValuesToServerKeywords = (typeKey: string, formValues: string[] = [], existingCategorised: any[] = []) => {
    const options = (PERSONAL_PROFILE_KEYWORDS as any)[typeKey] ?? [];
    const idx = buildServerKeywordIndex(existingCategorised);
    const out: string[] = [];

    formValues.forEach((fv) => {
      if (!fv || typeof fv !== "string") return;
      const nfv = normalize(fv);
      // try find server keyword that includes normalized option text
      let found = idx.find((sk) => normalize(sk).includes(nfv) || nfv.includes(normalize(sk)));
      if (!found) {
        // if no server keyword, try to find an option that matches and then try to find server keyword containing that option
        const opt = options.find((o: string) => normalize(o) === nfv || normalize(o).includes(nfv) || nfv.includes(normalize(o)));
        if (opt) {
          found = idx.find((sk) => normalize(sk).includes(normalize(opt)) || normalize(opt).includes(normalize(sk)));
        }
      }
      // if found use server slug, else fallback to original form value (may be accepted by backend)
      out.push(found || fv);
    });

    return Array.from(new Set(out));
  };

  // Map user-visible form values (labels) to server keyword slugs for a given
  // categorised keyword category. This is deterministic and uses the keyword
  // categories fetched from the API (keywordCategories) to find matching slugs
  // by label or slug. Falls back to the original form value when no slug found.
  const mapFormValuesToCategorySlugs = (categorySlug: string, formValues: string[] = []): string[] => {
    const category = getKeywordCategoryBySlug(categorySlug);
    if (!category || !Array.isArray(category.keywords) || !Array.isArray(formValues)) return formValues;
    const out: string[] = [];
    formValues.forEach((fv) => {
      if (!fv || typeof fv !== "string") return;
      const norm = String(fv).toLowerCase().trim();
      // try exact displayName match
      const exact = category.keywords.find((kw: any) => String(kw.displayName || kw.slug || "").toLowerCase() === norm);
      if (exact && exact.slug) {
        out.push(exact.slug);
        return;
      }
      // try inclusion match on displayName or slug
      const incl = category.keywords.find((kw: any) => {
        const label = String(kw.displayName || kw.slug || "").toLowerCase();
        const slug = String(kw.slug || "").toLowerCase();
        return label.includes(norm) || slug.includes(norm) || norm.includes(label) || norm.includes(slug);
      });
      if (incl && incl.slug) {
        out.push(incl.slug);
        return;
      }
      // fallback to original value
      out.push(fv);
    });
    return Array.from(new Set(out));
  };

  useEffect(() => {
    if (!isOpen || !currentUser) return;
    const currentId = (currentUser?.id ?? currentUser?.userID ?? null);
    // if we've already initialised the form for this user while modal is open, don't overwrite edits
    if (lastLoadedUserRef.current && String(lastLoadedUserRef.current) === String(currentId)) {
      return;
    }
    // choose sport profile that best matches the current event sport, fallback to first
    const sportProfile = pickSportProfile(currentUser.sportsProfiles, inferredSport ?? undefined);
    // prefer locale-specific personal profile (API returns array of profiles keyed by locale)
    const userPersonalProfile = pickProfileByLocale(currentUser.profiles, "en");
    const normalized = convertCategorisedKeywordsInput(
      userPersonalProfile?.categorisedKeywordsCanonical ?? userPersonalProfile?.categorisedKeywords ?? userPersonalProfile?.categorisedKeywords?.categorisedKeywords ?? userPersonalProfile?.categorisedKeywords?.mapping ?? userPersonalProfile?.keywords ?? []
    );
    const parsed = parseCategorisedKeywords(normalized ?? []);
    const currentEthnicity = parsed.ethnicity ?? [];
    const currentHeight = parsed.height_range ?? [];
    const currentCitizenship = parsed.citizenship_multi_select ?? [];
    const currentMedical = parsed.chronic_conditions ?? [];
    const currentAllergies = parsed.allergies ?? [];
    setSelectedCategorisedKeywords(buildCategorisedMappingForPreview(normalized ?? userPersonalProfile?.categorisedKeywords ?? null));
    setSelectedProfileBio(userPersonalProfile?.bio ?? null);
    setSelectedProfiles(currentUser.profiles ?? (userPersonalProfile ? [userPersonalProfile] : []));

    setForm((f: any) => ({
      ...f,
      firstName: currentUser.firstName ?? f.firstName,
      lastName: currentUser.lastName ?? f.lastName,
      email: currentUser.email ?? f.email,
      phone: currentUser.phone ?? currentUser.mobile ?? f.phone,
      dateOfBirth: formatDateToInput(currentUser.dob),
      gender: currentUser.gender ? [normalizeGender(currentUser.gender)] : f.gender,
      address: currentUser.address?.address ?? f.address,
      city: currentUser.address?.town ?? f.city,
      postalCode: currentUser.address?.postcode ?? currentUser.zip ?? f.postalCode,
      country: currentUser.address?.country ?? f.country,
      team: currentUser.selectedCompanyMembership?.company?.name ?? f.team,
      sportProfile: {
        ...f.sportProfile,
        sport: sportProfile?.sport || inferredSport || f.sportProfile.sport,
        sportId: sportProfile?.sportId ?? f.sportProfile.sportId,
        currentClub: sportProfile?.currentClub ?? f.sportProfile.currentClub,
        preferredPosition: sportProfile?.preferred_positions_by_sport?.[0]
          ? [sportProfile.preferred_positions_by_sport[0]]
          : f.sportProfile.preferredPosition,
        secondaryPosition: sportProfile?.secondary_positions_by_sport?.[0]
          ? [sportProfile.secondary_positions_by_sport[0]]
          : f.sportProfile.secondaryPosition,
        dominantFoot: sportProfile?.dominant_foot?.[0]
          ? [
              findMatchingApiSlug(
                sportProfile.dominant_foot[0],
                "dominant_foot",
              ),
            ].filter(Boolean)
          : f.sportProfile.dominantFoot,
        dominantHand: sportProfile?.dominant_hand?.[0]
          ? [
              findMatchingApiSlug(
                sportProfile.dominant_hand[0],
                "dominant_hand",
              ),
            ].filter(Boolean)
          : f.sportProfile.dominantHand,
        trainingFrequency: sportProfile?.training_frequency
          ? sportProfile.training_frequency
              .map((t: string) => findMatchingApiSlug(t, "training_frequency"))
              .filter(Boolean)
          : f.sportProfile.trainingFrequency,
        preferredTrainingStyle: sportProfile?.preferred_training_style
          ? sportProfile.preferred_training_style
              .map((s: string) =>
                findMatchingApiSlug(s, "preferred_training_style"),
              )
              .filter(Boolean)
          : f.sportProfile.preferredTrainingStyle,
        pastInjuries: sportProfile?.past_sports_injuries
          ? sportProfile.past_sports_injuries
              .map((i: string) => findMatchingApiSlug(i, "past_sports_injuries"))
              .filter(Boolean)
          : f.sportProfile.pastInjuries,
      },
      // personal profile keyword fields (map server keywords to display options)
      ethnicity: currentEthnicity.length
        ? mapServerKeywordsToFormValues("ethnicity", currentEthnicity, normalized)
        : f.ethnicity,
      height: currentHeight.length
        ? mapServerKeywordsToFormValues("height_range", currentHeight, normalized)
        : f.height,
      citizenship: currentCitizenship.length
        ? mapServerKeywordsToFormValues("citizenship_multi_select", currentCitizenship, normalized)
        : f.citizenship,
      medical: currentMedical.length
        ? mapServerKeywordsToFormValues("chronic_conditions", currentMedical, normalized)
        : f.medical,
      allergies: currentAllergies.length
        ? mapServerKeywordsToFormValues("allergies", currentAllergies, normalized)
        : f.allergies,
    }));
    // mark as initialised for this user so we don't clobber edits
    try {
      if (currentId) lastLoadedUserRef.current = String(currentId);
    } catch (e) {}
  }, [isOpen, currentUser]);

  // Apply initial submission target (e.g. "Family Member") when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (initialSubmittedFor) {
      setForm((f) => ({ ...f, submittedFor: initialSubmittedFor }));
    }
  }, [isOpen, initialSubmittedFor]);

  const familyOptions: MultiSelectOption[] = useMemo(() => {
    if (!Array.isArray(familyData)) return [];

    const map = new Map<string, MultiSelectOption>();
    const awaitingSet = new Set((awaitingRequesterIds || []).map(String));

    const makeKey = (id: string | null, label: string) => {
      if (id) return String(id);
      return label || "";
    };

    const preferLabel = (a: string, b: string, relA: boolean, relB: boolean) => {
      if (relA && !relB) return a;
      if (relB && !relA) return b;
      return a.length >= b.length ? a : b;
    };

    const push = (id: string | null, label: string, hasRel = false, isAwaiting = false) => {
      const key = makeKey(id, label);
      if (!key) return;
      if (currentUser && id && String(currentUser.id) === String(id)) return; // don't include self

      // append awaiting suffix to label if needed
      let outLabel = label;
      if (isAwaiting && !/awaiting approval/i.test(outLabel)) {
        outLabel = `${outLabel} (Awaiting Approval)`;
      }

      const existing = map.get(key);
      if (!existing) {
        map.set(key, { value: id ? String(id) : label, label: outLabel, disabled: !!isAwaiting });
        return;
      }

      const existingHasRel = /\(.+\)$/.test(existing.label);
      const chosenLabel = preferLabel(existing.label, outLabel, existingHasRel, hasRel);
      map.set(key, { value: existing.value || (id ? String(id) : label), label: chosenLabel, disabled: !!(existing.disabled || isAwaiting) });
    };

    familyData.forEach((item: any) => {
      if (!item) return;
      // relationship object
      if (Array.isArray(item.users)) {
        const relLabel =
          item.familyRelationshipToThem || item.familyRelationship || item.relationship || item.label || item.type || "";
        item.users.forEach((ur: any) => {
          const u = ur?.user ?? ur;
          if (!u) return;
          const id = u.id ?? u.userID ?? u._id ?? u.value ?? null;
          const name = u.fullName || u.name || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || String(id ?? "");
          const label = relLabel ? `${name} (${relLabel})` : name;
          const status = u?.requesterMembership?.status ?? u?.requesterMemberships?.[0]?.status ?? u?.requestMembership?.status ?? u?.requestStatus ?? null;
          const isAwaitingLocal = String(status ?? "").toUpperCase() === "AWAITING";
          const finalAwaiting = id ? (isAwaitingLocal || awaitingSet.has(String(id))) : isAwaitingLocal;
          push(id ? String(id) : null, label, Boolean(relLabel), !!finalAwaiting);
        });
        return;
      }

      if (typeof item === "object") {
        const id = item.id ?? item.userID ?? item._id ?? item.value ?? null;
        const name = item.fullName || item.name || `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || String(id ?? "");
        const relLabel = item.relationship || item.familyRelationship || item.type || "";
        const label = relLabel ? `${name} (${relLabel})` : name;
        const status = item?.requesterMembership?.status ?? item?.requesterMemberships?.[0]?.status ?? item?.requestMembership?.status ?? item?.requestStatus ?? null;
        const isAwaitingLocal = String(status ?? "").toUpperCase() === "AWAITING";
        const finalAwaiting = id ? (isAwaitingLocal || awaitingSet.has(String(id))) : isAwaitingLocal;
        push(id ? String(id) : null, label, Boolean(relLabel), !!finalAwaiting);
        return;
      }

      // primitive
      const id = String(item);
      const finalAwaiting = awaitingSet.has(String(id));
      push(id, id, false, !!finalAwaiting);
    });

    const opts = Array.from(map.values());
    opts.sort((a, b) => a.label.localeCompare(b.label));
    // include any ad-hoc created members (kept in state)
    if (additionalFamilyMembers && additionalFamilyMembers.length) {
      additionalFamilyMembers.forEach((m: any) => {
        if (!opts.find((o) => o.value === m.id)) {
          const isAwait = awaitingSet.has(String(m.id));
          opts.push({ value: m.id, label: isAwait ? `${m.label} (Awaiting Approval)` : m.label, disabled: isAwait });
        }
      });
    }
    // add a special action item to open create-new modal
    opts.push({ value: "__new_family_member__", label: "Add new family member…" });
    return opts;
  }, [familyData, currentUser, awaitingRequesterIds]);

  // Debug: when modal opens, log raw family data and computed options
  useEffect(() => {
    if (!isOpen) return;
    try {
      // eslint-disable-next-line no-console
      console.info("RequestModal: raw familyData:", familyData);
      // eslint-disable-next-line no-console
      console.info("RequestModal: computed familyOptions:", familyOptions);
      // eslint-disable-next-line no-console
      console.info("RequestModal: awaitingRequesterIds:", awaitingRequesterIds);
    } catch (e) {
      /* ignore */
    }
  }, [isOpen, familyData, familyOptions, awaitingRequesterIds]);

  // If the currently selected family member is awaiting approval, clear the selection
  // so the user cannot submit for them. Runs whenever awaitingRequesterIds or form.familyMember changes.
  useEffect(() => {
    if (!isOpen) return;
    try {
      const selectedId = form.submittedFor === "Family Member" && form.familyMember?.[0] ? String(form.familyMember[0]) : null;
      if (selectedId && awaitingRequesterIds && awaitingRequesterIds.map(String).includes(selectedId)) {
        setForm((f: any) => ({ ...f, familyMember: [] }));
        setApiMessage({ variant: "error", text: "Selected family member is awaiting approval and cannot be submitted." });
        window.setTimeout(() => setApiMessage(null), 4000);
      }
    } catch (e) {
      /* ignore */
    }
  }, [isOpen, form.familyMember, awaitingRequesterIds]);
  

  // GraphQL query for user details (server returns JSON blob)
  const GET_USER_DETAILS = gql`
    query getUserDetails($userID: String!) {
      getUserDetails(userID: $userID)
    }
  `;

  const fetchUserDetails = async (userId?: string | null) => {
    if (!userId) return null;
    try {
      const { data } = await apolloClient.query({
        query: GET_USER_DETAILS,
        variables: { userID: userId },
        fetchPolicy: "no-cache",
      });
      return data?.getUserDetails ?? null;
    } catch (err) {
      console.error("getUserDetails failed", err);
      return null;
    }
  };

  // When selection changes (Myself vs Family Member) populate form from selected user
  useEffect(() => {
    if (!isOpen) return;
    const mapDetailsToForm = (details: any) => {
      if (!details) return null;
      const sportProfileFromDetails = pickSportProfile(details.sportsProfiles ?? [], inferredSport ?? undefined) || details.sportProfile || {};
      // extract personal profile keyword categories from details (if present) - prefer locale 'en'
      const detailsPersonalProfile = pickProfileByLocale(details.profiles ?? [], "en") ?? details.profile ?? null;
      const normalized = convertCategorisedKeywordsInput(
        detailsPersonalProfile?.categorisedKeywordsCanonical ?? detailsPersonalProfile?.categorisedKeywords ?? detailsPersonalProfile?.categorisedKeywords?.mapping ?? detailsPersonalProfile?.keywords ?? []
      );
      const parsed = parseCategorisedKeywords(normalized ?? []);
      const detailsEthnicity = parsed.ethnicity ?? [];
      const detailsHeight = parsed.height_range ?? [];
      const detailsCitizenship = parsed.citizenship_multi_select ?? [];
      const detailsMedical = parsed.chronic_conditions ?? [];
      const detailsAllergies = parsed.allergies ?? [];
      // store raw categorisedKeywords and bio to use when building update payload
      if (detailsPersonalProfile) {
        setSelectedCategorisedKeywords(buildCategorisedMappingForPreview(normalized ?? detailsPersonalProfile.categorisedKeywords ?? null));
        setSelectedProfileBio(detailsPersonalProfile.bio ?? null);
        setSelectedProfiles(details.profiles ?? (detailsPersonalProfile ? [detailsPersonalProfile] : []));
        // if profile has a locale, set selectedLocale to it
        const profLocale = detailsPersonalProfile?.locale ?? (details.profiles && details.profiles[0]?.locale) ?? "en";
        setSelectedLocale(profLocale);
      }
      const preferredPosition = sportProfileFromDetails.preferred_positions_by_sport
        ? sportProfileFromDetails.preferred_positions_by_sport.map((p: any) => String(p))
        : sportProfileFromDetails.preferredPositions || [];
      const secondaryPosition = sportProfileFromDetails.secondary_positions_by_sport
        ? sportProfileFromDetails.secondary_positions_by_sport.map((p: any) => String(p))
        : sportProfileFromDetails.secondaryPositions || [];

      const dominantFoot = sportProfileFromDetails.dominant_foot
        ? [findMatchingApiSlug(sportProfileFromDetails.dominant_foot[0], "dominant_foot")].filter(Boolean)
        : (sportProfileFromDetails.dominantFoot || []);
      const dominantHand = sportProfileFromDetails.dominant_hand
        ? [findMatchingApiSlug(sportProfileFromDetails.dominant_hand[0], "dominant_hand")].filter(Boolean)
        : (sportProfileFromDetails.dominantHand || []);

      const trainingFrequency = sportProfileFromDetails.training_frequency
        ? sportProfileFromDetails.training_frequency.map((t: string) => findMatchingApiSlug(t, "training_frequency")).filter(Boolean)
        : (sportProfileFromDetails.trainingFrequency || []);

      const preferredTrainingStyle = sportProfileFromDetails.preferred_training_style
        ? sportProfileFromDetails.preferred_training_style.map((s: string) => findMatchingApiSlug(s, "preferred_training_style")).filter(Boolean)
        : (sportProfileFromDetails.preferredTrainingStyle || []);

      const pastInjuries = sportProfileFromDetails.past_sports_injuries
        ? sportProfileFromDetails.past_sports_injuries.map((i: string) => findMatchingApiSlug(i, "past_sports_injuries")).filter(Boolean)
        : (sportProfileFromDetails.pastInjuries || []);

    // Decide which sport to populate the form with:
    // - If the event infers a specific sport (not the generic 'sports'), enforce that sport so the user sees the matching profile.
    // - If the event is the generic SPORTS_CLUB (mapped to 'sports'), allow any sports profile to be shown/selected.
    const isGenericSportsEvent = inferredSport === "sports";
    const sportForForm = !inferredSport
      ? (details.selectedSport ?? sportProfileFromDetails.sport ?? "")
      : (isGenericSportsEvent ? (details.selectedSport ?? sportProfileFromDetails.sport ?? inferredSport ?? "") : inferredSport);

    return {
      firstName: details.firstName ?? details.first_name ?? details.fullName ?? "",
      lastName: details.lastName ?? details.last_name ?? "",
      email: details.email ?? details.contactEmail ?? "",
      phone: details.phone ?? details.mobile ?? details.contactPhone ?? "",
      dateOfBirth: formatDateToInput(details.dob ?? details.dateOfBirth ?? details.DOB),
      gender: details.gender ? [normalizeGender(details.gender)] : [],
      address: details.address?.address ?? details.address?.street ?? "",
      city: details.address?.town ?? details.address?.city ?? "",
      postalCode: details.address?.postcode ?? details.zip ?? "",
      country: details.address?.country ?? "",
      sportProfile: {
        sport: sportForForm,
        sportId: details.sportsProfileId ?? sportProfileFromDetails.sportId ?? "",
        currentClub: sportProfileFromDetails.currentClub ?? "",
        preferredPosition: preferredPosition.length ? preferredPosition : [],
        secondaryPosition: secondaryPosition.length ? secondaryPosition : [],
        dominantFoot,
        dominantHand,
        trainingFrequency,
        preferredTrainingStyle,
        pastInjuries,
      },
        // personal profile keyword fields from fetched details (mapped to display values)
        ethnicity: detailsEthnicity.length
            ? mapServerKeywordsToFormValues("ethnicity", detailsEthnicity, normalized)
            : [],
          height: detailsHeight.length
            ? mapServerKeywordsToFormValues("height_range", detailsHeight, normalized)
            : [],
          citizenship: detailsCitizenship.length
            ? mapServerKeywordsToFormValues("citizenship_multi_select", detailsCitizenship, normalized)
            : [],
          medical: detailsMedical.length
            ? mapServerKeywordsToFormValues("chronic_conditions", detailsMedical, normalized)
            : [],
          allergies: detailsAllergies.length
            ? mapServerKeywordsToFormValues("allergies", detailsAllergies, normalized)
            : [],
      };
    };

    const loadForSelected = async () => {
      try {
        const selectedId = form.submittedFor === "Family Member" ? form.familyMember?.[0] : currentUser?.id;
        // if we've already loaded this user's details for this open modal, skip reloading
        if (!selectedId) return;
        if (lastLoadedUserRef.current && String(lastLoadedUserRef.current) === String(selectedId)) {
          return;
        }

        // prefer currentUser object already available when asking for self
        if (form.submittedFor === "Myself" && currentUser && String(currentUser.id) === String(selectedId)) {
          const mapped = mapDetailsToForm(currentUser);
          if (mapped) {
            setForm((f: any) => ({ ...f, ...mapped, familyMember: [] }));
            lastLoadedUserRef.current = String(selectedId);
          }
          return;
        }

        const details = await fetchUserDetails(selectedId);
        if (!details) return;
        const mapped = mapDetailsToForm(details);
        if (mapped) {
          setForm((f: any) => ({ ...f, ...mapped }));
          lastLoadedUserRef.current = String(selectedId);
        }
      } catch (err) {
        console.error("Failed to load selected user details", err);
      }
    };

    loadForSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.submittedFor, form.familyMember, isOpen, currentUser]);

  // Clear last-loaded marker whenever modal is (re)opened so initial population runs
  useEffect(() => {
    if (isOpen) {
      lastLoadedUserRef.current = null;
    }
  }, [isOpen]);

  const handleSaveProfile = async () => {
    try {
      const targetUserId =
        form.submittedFor === "Family Member" && form.familyMember?.[0]
          ? form.familyMember[0]
          : currentUser?.id;

      const gender = form.gender?.[0] ? form.gender[0].toUpperCase() : "";

      const address = {
        address: form.address || "",
        country: form.country || "",
        town: form.city || "",
        city: form.city || "",
        postcode: form.postalCode || "",
      };

      // Build profiles.categorisedKeywords preserving existing PERSONAL bucket and
      // replacing/adding typed buckets for personal keywords from the form.
      const existingCategorised = Array.isArray(selectedCategorisedKeywords)
        ? [...selectedCategorisedKeywords]
        : [];

      // helper to set or replace a typed bucket
      const upsertTypedBucket = (arr: any[], type: string, keywords: string[]) => {
        if (!Array.isArray(keywords) || !keywords.length) return;
        const idx = arr.findIndex((c) => String(c?.type) === String(type));
        const bucket = { type, keywords };
        if (idx >= 0) arr[idx] = bucket;
        else arr.push(bucket);
      };

      // For legacy API compatibility the API expects personal fields to be nested
      // under a PERSONAL bucket with a mapping of subkeys -> arrays. Build or
      // update that nested structure here for the PERSONAL-type fields.
      const personalFields = [
        "ethnicity",
        "height_range",
        "citizenship_multi_select",
        "chronic_conditions",
        "allergies",
      ];

      // find or create PERSONAL bucket
      let personalIdx = existingCategorised.findIndex((c) => String(c?.type) === "PERSONAL");
      if (personalIdx < 0) {
        existingCategorised.push({ type: "PERSONAL" });
        personalIdx = existingCategorised.length - 1;
      }

      // Build a personal mapping by merging any existing PERSONAL shape (either
      // { keywords: { ... } } or { ethnicity: [...], ... }) so we preserve prior
      // data, then overwrite with current form values.
      const existingPersonal = existingCategorised[personalIdx] || { type: "PERSONAL" };
      const personalMap: any = {};
      // merge legacy .keywords object if present
      if (existingPersonal && existingPersonal.keywords && typeof existingPersonal.keywords === "object" && !Array.isArray(existingPersonal.keywords)) {
        Object.assign(personalMap, existingPersonal.keywords);
      }
      // merge any known typed fields that may already exist directly on PERSONAL
      for (const k of [
        "ethnicity",
        "height_range",
        "citizenship_multi_select",
        "chronic_conditions",
        "allergies",
      ]) {
        if (Array.isArray(existingPersonal[k])) {
          personalMap[k] = Array.from(new Set([...(personalMap[k] || []), ...existingPersonal[k]]));
        }
      }

      // Prefer deterministic mapping to category slugs when keywordCategories are available
      personalMap.ethnicity = mapFormValuesToCategorySlugs("ethnicity", form.ethnicity || []);
      personalMap.height_range = mapFormValuesToCategorySlugs("height_range", form.height || []);
      personalMap.citizenship_multi_select = mapFormValuesToCategorySlugs("citizenship_multi_select", form.citizenship || []);
      personalMap.chronic_conditions = mapFormValuesToCategorySlugs("chronic_conditions", form.medical || []);
      personalMap.allergies = mapFormValuesToCategorySlugs("allergies", form.allergies || []);

      // replace PERSONAL bucket with a mapping object (no nested `keywords`)
      existingCategorised[personalIdx] = { type: "PERSONAL", ...personalMap };

      // remove any duplicate flat typed buckets for these personal fields
      for (const t of personalFields) {
        const idx = existingCategorised.findIndex((c) => String(c?.type) === String(t));
        if (idx >= 0) existingCategorised.splice(idx, 1);
      }

      // Convert existingCategorised (legacy array or mapping) into a
      // mapping shape accepted by the server: { TYPE: { categoryKey: [items...] } }
      const buildCategorisedMapping = (input: any[] | null) => {
        const mapping: Record<string, any> = {};
        if (!input) return mapping;
        // If input is already an object mapping (unlikely here), copy it
        if (!Array.isArray(input) && typeof input === "object") {
          return { ...(input as Record<string, any>) };
        }
        // Array form: [{ type, keywords: [...] } or { type, ...subkeys }]
        (input || []).forEach((c: any) => {
          if (!c || !c.type) return;
          const type = String(c.type);
          if (!mapping[type]) mapping[type] = {};
          // if this entry has a keywords array, place it under a 'keywords' category
          if (Array.isArray(c.keywords) && c.keywords.length) {
            mapping[type]["keywords"] = Array.from(new Set([...(mapping[type]["keywords"] || []), ...c.keywords]));
          }
          // copy any other subkeys (e.g., ethnicity, allergies) into the mapping
          Object.keys(c).forEach((k) => {
            if (k === "type" || k === "keywords") return;
            const val = c[k];
            if (!val) return;
            mapping[type][k] = Array.isArray(val) ? Array.from(new Set([...(mapping[type][k] || []), ...val])) : [String(val)];
          });
        });
        return mapping;
      };

      const categorisedMapping = buildCategorisedMapping(existingCategorised);

      // merge/overwrite PERSONAL subkeys with current form values
      categorisedMapping["PERSONAL"] = categorisedMapping["PERSONAL"] || {};
      categorisedMapping["PERSONAL"].ethnicity = mapFormValuesToCategorySlugs("ethnicity", form.ethnicity || []);
      categorisedMapping["PERSONAL"].height_range = mapFormValuesToCategorySlugs("height_range", form.height || []);
      categorisedMapping["PERSONAL"].citizenship_multi_select = mapFormValuesToCategorySlugs("citizenship_multi_select", form.citizenship || []);
      categorisedMapping["PERSONAL"].chronic_conditions = mapFormValuesToCategorySlugs("chronic_conditions", form.medical || []);
      categorisedMapping["PERSONAL"].allergies = mapFormValuesToCategorySlugs("allergies", form.allergies || []);

      // Ensure per-sport metadata is included under a SPORTS mapping so previews
      // and update payloads carry full sports-profile fields nested by sport.
      try {
        // Decide sport key for per-sport mappings and payloads.
        // - If the event maps to a specific sport (e.g. football_or_soccer), enforce that sport.
        // - If the event is the generic SPORTS_CLUB (mapped to 'sports'), allow the user-selected sportProfile.sport to be used if present.
        const isGenericSportsEvent = inferredSport === "sports";
        const sportKey = isGenericSportsEvent
          ? (form.sportProfile?.sport || "sports")
          : (inferredSport || form.sportProfile?.sport || "");

        if (sportKey) {
          categorisedMapping["SPORTS"] = categorisedMapping["SPORTS"] || {};
          categorisedMapping["SPORTS"][sportKey] = {
            sport: sportKey,
            sportId: form.sportProfile.sportId || "",
            sportPic: form.sportProfile.sportPic || "",
            trialVideo: form.sportProfile.trialVideo || "",
            isActive: typeof form.sportProfile.isActive !== "undefined" ? form.sportProfile.isActive : true,
            currentClub: form.sportProfile.currentClub || "",
            otherClubs: form.sportProfile.otherClubs || [],
            preferred_positions_by_sport: form.sportProfile.preferredPosition || [],
            preferred_positions_short: form.sportProfile.preferred_positions_short || [],
            preferred_training_style: form.sportProfile.preferredTrainingStyle || [],
            protective_gear_used: form.sportProfile.protective_gear_used || [],
            secondary_positions_by_sport: form.sportProfile.secondaryPosition || [],
            secondary_positions_short: form.sportProfile.secondary_positions_short || [],
            dominant_foot: form.sportProfile.dominantFoot || [],
            dominant_hand: form.sportProfile.dominantHand || [],
            supplements_used: form.sportProfile.supplements_used || [],
            training_frequency: form.sportProfile.trainingFrequency || [],
            past_sports_injuries: form.sportProfile.pastInjuries || [],
            walking_frequency_over_30mins: form.sportProfile.walking_frequency_over_30mins || [],
          };
        }
      } catch (e) {
        /* ignore */
      }

      let profiles: any[] = [];
      if (Array.isArray(selectedProfiles) && selectedProfiles.length) {
        profiles = selectedProfiles.map((p: any) => ({
          ...p,
          locale: p?.locale || selectedLocale || "en",
          bio: selectedProfileBio ?? p?.bio ?? null,
          keywords: p?.keywords ?? [],
          categorisedKeywords: categorisedMapping,
        }));
      } else {
        profiles = [
          { locale: selectedLocale || "en", bio: selectedProfileBio ?? null, keywords: [], categorisedKeywords: categorisedMapping },
        ];
      }

      // Update local preview state so the UI reflects current form values immediately
      try {
        setSelectedCategorisedKeywords(buildCategorisedMappingForPreview(categorisedMapping));
        setSelectedProfileBio(selectedProfileBio ?? null);
        setSelectedProfiles(profiles);
      } catch (e) {
        /* ignore */
      }

      // Recompute sportKey here to ensure payload matches the per-sport mapping above.
      const isGenericSportsEvent = inferredSport === "sports";
      const sportKey = isGenericSportsEvent
        ? (form.sportProfile?.sport || "sports")
        : (inferredSport || form.sportProfile?.sport || "");

      const sportsProfiles = [
        {
          sport: sportKey,
          sportId: form.sportProfile.sportId || "",
          currentClub: form.sportProfile.currentClub || "",
          preferred_positions_by_sport:
            form.sportProfile.preferredPosition || [],
          secondary_positions_by_sport:
            form.sportProfile.secondaryPosition || [],
          dominant_foot: form.sportProfile.dominantFoot || [],
          dominant_hand: form.sportProfile.dominantHand || [],
          training_frequency: form.sportProfile.trainingFrequency || [],
          preferred_training_style: form.sportProfile.preferredTrainingStyle || [],
          past_sports_injuries: form.sportProfile.pastInjuries || [],
        },
      ];

      // Debug log final payload variables
      const variablesToSend = {
        userId: targetUserId,
        gender,
        dob: form.dateOfBirth || null,
        address,
        deliveryAddress: address,
        passportNumber: "",
        nationalSecurityNumber: "",
        avatar: "",
        profiles,
        secondaryProfilePic: null,
        sportsProfiles,
        sportsProfileId: form.sportProfile.sportId || null,
        selectedSport: sportKey,
        notes: form.notes,
      };
      console.info("updateUserProfile payload (saveProfile):", variablesToSend);

      const resp = await updateUserProfile(variablesToSend);
      console.info("updateUserProfile response (saveProfile):", resp);

      if (resp?.data?.updateUserProfile) {
        addToast?.({ variant: "success", title: "Profile saved" });
        // refresh client-side cached user data so `me` and `getUserDetails` reflect changes
        try {
          const meRes = await apolloClient.query({ query: ME, fetchPolicy: "network-only" });
          console.info("ME after update (saveProfile):", meRes?.data?.me ?? meRes);
        } catch (e) {
          console.warn("Failed to refetch ME after updateUserProfile", e);
        }
        try {
          const detailsRes = await apolloClient.query({ query: GET_USER_DETAILS, variables: { userID: targetUserId }, fetchPolicy: "network-only" });
          console.info("getUserDetails after update (saveProfile):", detailsRes?.data?.getUserDetails ?? detailsRes);
        } catch (e) {
          console.warn("Failed to refetch getUserDetails after updateUserProfile", e);
        }
      } else {
        setApiMessage({ variant: "error", text: "Failed to save profile." });
        window.setTimeout(() => setApiMessage(null), 5000);
      }
    } catch (err) {
      console.error("Failed to save profile", err);
      setApiMessage({ variant: "error", text: "Failed to save profile." });
      try {
        window.setTimeout(() => setApiMessage(null), 5000);
      } catch (e) {}
    }
  };

  

  const [activeProfileTab, setActiveProfileTab] = useState<"user" | "sport">(
    "user",
  );

  const makeOptions = (key: keyof typeof PERSONAL_PROFILE_KEYWORDS) =>
    (PERSONAL_PROFILE_KEYWORDS[key] ?? []).map((v: string) => ({
      value: v,
      label: v,
    }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (joinStateLoading || joinStateError || !joinState) {
      setApiMessage({
        variant: "error",
        text: "Your registration status could not be verified. Please reopen the form and try again.",
      });
      return;
    }
    if (!form.termsAccepted || !form.videoConsent || !form.socialMediaConsent) {
      setApiMessage({
        variant: "error",
        text: "You must accept all consent options before submitting.",
      });
      try {
        window.setTimeout(() => setApiMessage(null), 5000);
      } catch (e) {
        /* ignore */
      }
      return;
    }
    try {
      const targetUserId =
        form.submittedFor === "Family Member" && form.familyMember?.[0]
          ? form.familyMember[0]
          : currentUser?.id;

      const gender = form.gender?.[0] ? form.gender[0].toUpperCase() : "";

      const address = {
        address: form.address || "",
        country: form.country || "",
        town: form.city || "",
        city: form.city || "",
        postcode: form.postalCode || "",
      };

      const existingCategorisedSubmit = Array.isArray(selectedCategorisedKeywords)
        ? [...selectedCategorisedKeywords]
        : [];

      const buildCategorisedMapping = (input: any[] | null) => {
        const mapping: Record<string, any> = {};
        if (!input) return mapping;
        if (!Array.isArray(input) && typeof input === "object") return { ...(input as Record<string, any>) };
        (input || []).forEach((c: any) => {
          if (!c || !c.type) return;
          const type = String(c.type);
          if (!mapping[type]) mapping[type] = {};
          if (Array.isArray(c.keywords) && c.keywords.length) {
            mapping[type]["keywords"] = Array.from(new Set([...(mapping[type]["keywords"] || []), ...c.keywords]));
          }
          Object.keys(c).forEach((k) => {
            if (k === "type" || k === "keywords") return;
            const val = c[k];
            if (!val) return;
            mapping[type][k] = Array.isArray(val) ? Array.from(new Set([...(mapping[type][k] || []), ...val])) : [String(val)];
          });
        });
        return mapping;
      };

      const categorisedMappingSubmit = buildCategorisedMapping(existingCategorisedSubmit);
      categorisedMappingSubmit["PERSONAL"] = categorisedMappingSubmit["PERSONAL"] || {};
      categorisedMappingSubmit["PERSONAL"].ethnicity = mapFormValuesToCategorySlugs("ethnicity", form.ethnicity || []);
      categorisedMappingSubmit["PERSONAL"].height_range = mapFormValuesToCategorySlugs("height_range", form.height || []);
      categorisedMappingSubmit["PERSONAL"].citizenship_multi_select = mapFormValuesToCategorySlugs("citizenship_multi_select", form.citizenship || []);
      categorisedMappingSubmit["PERSONAL"].chronic_conditions = mapFormValuesToCategorySlugs("chronic_conditions", form.medical || []);
      categorisedMappingSubmit["PERSONAL"].allergies = mapFormValuesToCategorySlugs("allergies", form.allergies || []);

      let profiles: any[] = [];
      if (Array.isArray(selectedProfiles) && selectedProfiles.length) {
        profiles = selectedProfiles.map((p: any) => ({
          ...p,
          locale: p?.locale || selectedLocale || "en",
          bio: selectedProfileBio ?? p?.bio ?? null,
          keywords: p?.keywords ?? [],
          categorisedKeywords: categorisedMappingSubmit,
        }));
      } else {
        profiles = [
          { locale: selectedLocale || "en", bio: selectedProfileBio ?? null, keywords: [], categorisedKeywords: categorisedMappingSubmit },
        ];
      }

      // Update local preview state so the UI reflects current form values immediately
      try {
        setSelectedCategorisedKeywords(buildCategorisedMappingForPreview(categorisedMappingSubmit));
        setSelectedProfileBio(selectedProfileBio ?? null);
        setSelectedProfiles(profiles);
      } catch (e) {
        /* ignore */
      }

      // Decide sportKey for submit payloads (same rule as saveProfile):
      // - enforce inferred sport for specific event types
      // - allow user-selected sport for generic 'sports' event types
      const isGenericSportsEvent = inferredSport === "sports";
      const sportKey = isGenericSportsEvent
        ? (form.sportProfile?.sport || "sports")
        : (inferredSport || form.sportProfile?.sport || "");

      const sportsProfiles = [
        {
          sport: sportKey,
          sportId: form.sportProfile.sportId || "",
          currentClub: form.sportProfile.currentClub || "",
          preferred_positions_by_sport:
            form.sportProfile.preferredPosition || [],
          secondary_positions_by_sport:
            form.sportProfile.secondaryPosition || [],
          dominant_foot: form.sportProfile.dominantFoot || [],
          dominant_hand: form.sportProfile.dominantHand || [],
          training_frequency: form.sportProfile.trainingFrequency || [],
          preferred_training_style:
            form.sportProfile.preferredTrainingStyle || [],
          past_sports_injuries: form.sportProfile.pastInjuries || [],
        },
      ];

      const variablesToSend = {
        userId: targetUserId,
        gender,
        dob: form.dateOfBirth || null,
        address,
        deliveryAddress: address,
        passportNumber: "",
        nationalSecurityNumber: "",
        avatar: "",
        profiles,
        secondaryProfilePic: null,
        sportsProfiles,
        sportsProfileId: form.sportProfile.sportId || null,
        selectedSport: sportKey,
        notes: form.notes,
      };
      console.info("updateUserProfile payload (submit):", variablesToSend);

      const response = await updateUserProfile(variablesToSend);
      console.info("updateUserProfile response (submit):", response);

      if (response?.data?.updateUserProfile) {
        try {
          addToast({ variant: "success", title: "Profile updated" });
        } catch (e) {
          /* ignore */
        }
        // refresh me/getUserDetails so UI updates reflect persisted values
        let meUserId = currentUser?.id;
        try {
          const meRes = await apolloClient.query({ query: ME, fetchPolicy: "network-only" });
          const me = meRes?.data?.me;
          meUserId = me?.id ?? me?.userID ?? me?.userId ?? meUserId;
          console.info("ME after update (submit):", me ?? meRes);
        } catch (e) {
          console.warn("Failed to refetch ME after updateUserProfile (submit)", e);
        }
        try {
          const detailsRes = await apolloClient.query({ query: GET_USER_DETAILS, variables: { userID: targetUserId }, fetchPolicy: "network-only" });
          console.info("getUserDetails after update (submit):", detailsRes?.data?.getUserDetails ?? detailsRes);
        } catch (e) {
          console.warn("Failed to refetch getUserDetails after updateUserProfile (submit)", e);
        }
        try {
          const isFamilySubmission = form.submittedFor === "Family Member";
          if (
            currentEvent &&
            (isFamilySubmission ||
              !["ACCEPTED", "PENDING"].includes(
                (attendeeStatus || "").toUpperCase(),
              ))
          ) {
            const familyMemberId =
              isFamilySubmission && form.familyMember?.[0]
                ? form.familyMember[0]
                : null;
            const companyMembershipId =
              currentUser?.selectedCompanyMembership?.id ||
              currentUser?.selectedCompanyMembership?.companyId ||
              null;

            const normalizedMeUserId =
              meUserId != null ? String(meUserId) : null;
            // For family submissions the server treats `userId` as the guardian and
            // adds both the guardian and `familyMemberId` as trialists in one call.
            if (isFamilySubmission && normalizedMeUserId == null) {
              throw new Error("Unable to identify the current user from ME.");
            }
            const requests = isFamilySubmission
              ? [
                  {
                    userId: normalizedMeUserId,
                    isForSelf: false,
                    familyMemberId,
                  },
                ]
              : [
                  {
                    userId: targetUserId,
                    isForSelf: true,
                    familyMemberId: null,
                  },
                ];

            const refreshAttendanceLists = async () => {
              try {
                if (currentEvent?.id) {
                  await apolloClient.query({
                    query: GET_REQUEST_TO_JOIN,
                    variables: { eventId: currentEvent.id },
                    fetchPolicy: "network-only",
                  });
                }
              } catch (e) {
                console.warn("Failed to refetch request-to-join list:", e);
              }
              try {
                onSuccess && onSuccess();
              } catch (e) {
                /* ignore */
              }
            };

            let attendanceRequestSucceeded = false;
            try {
              for (const request of requests) {
                const trialResp = await requestEventAttendance({
                  eventId: currentEvent?.id ?? currentEvent?.eventID,
                  userId: request.userId,
                  isForSelf: request.isForSelf,
                  familyMemberId: request.familyMemberId,
                  companyMembershipId,
                  termsAccepted: !!form.termsAccepted,
                  videoConsent: !!form.videoConsent,
                  socialMediaConsent: !!form.socialMediaConsent,
                });

                if (!trialResp?.data?.requestEventAttendance) {
                  throw new Error("Failed to request club trial.");
                }
                attendanceRequestSucceeded = true;
              }

              try {
                addToast({ variant: "success", title: "Trial requested" });
              } catch (e) {
                /* ignore */
              }
              await refreshAttendanceLists();
            } catch (err) {
              console.error("Failed to request club trial", err);
              if (attendanceRequestSucceeded) {
                await refreshAttendanceLists();
              }
              setApiMessage({
                variant: "error",
                text: "Failed to request club trial.",
              });
              try {
                window.setTimeout(() => setApiMessage(null), 5000);
              } catch (e) {
                /* ignore */
              }
            }
          }
        } catch (err) {
          console.error("Failed to request club trial", err);
          setApiMessage({
            variant: "error",
            text: "Failed to request club trial.",
          });
          try {
            window.setTimeout(() => setApiMessage(null), 5000);
          } catch (e) {
            /* ignore */
          }
        }

        onClose();
      } else {
        console.error("Failed to update user profile", response);
        setApiMessage({
          variant: "error",
          text: "Failed to update user profile.",
        });
        try {
          window.setTimeout(() => setApiMessage(null), 5000);
        } catch (e) {
          /* ignore */
        }
      }
    } catch (err) {
      console.error("Failed to update user profile for trial request", err);
      setApiMessage({
        variant: "error",
        text: "Failed to update user profile.",
      });
      try {
        window.setTimeout(() => setApiMessage(null), 5000);
      } catch (e) {
        /* ignore */
      }
    }
  }

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="Register Request"
      size="lg"
      footer={
        <div className="mt-6 flex justify-end gap-3 border-t border-neutral-100 pt-4 dark:border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-neutral-200 px-5 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveProfile}
            className="rounded-xl border border-neutral-200 px-5 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-300"
          >
            Save Profile
          </button>
          <button
            type="submit"
            form="add-trial-form"
            disabled={
              joinStateLoading || Boolean(joinStateError) || !joinState ||
              !(form.termsAccepted && form.videoConsent && form.socialMediaConsent)
            }
            className={
              "rounded-xl px-5 py-2 text-sm font-semibold text-white " +
              (form.termsAccepted &&
              form.videoConsent &&
              form.socialMediaConsent
                ? ""
                : "cursor-not-allowed opacity-40")
            }
            style={{ backgroundColor: "var(--sidebar-bg)" }}
          >
            Submit Request
          </button>
        </div>
      }
    >
      <NewFamilyMemberModal
        isOpen={isNewFamilyOpen}
        onClose={(created) => {
          setIsNewFamilyOpen(false);
          if (created) {
            setAdditionalFamilyMembers((s) => [...s, created]);
            setForm((f: any) => ({ ...f, familyMember: [created.id] }));
          }
        }}
      />
      <form id="add-trial-form" onSubmit={handleSubmit} className="space-y-4">
        {joinStateError && (
          <p role="alert" className="text-sm text-red-600">
            Unable to load your registration status: {joinStateError.message}
          </p>
        )}
        {apiMessage && (
          <div
            className={
              "mb-3 rounded-md px-3 py-2 text-sm " +
              (apiMessage.variant === "success"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-red-100 text-red-800")
            }
          >
            {apiMessage.text}
          </div>
        )}
        <div className="flex gap-4">
          {(["Myself", "Family Member"] as const).map((opt) => (
            <label
              key={opt}
              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              <input
                type="radio"
                name="submittedFor"
                value={opt}
                checked={form.submittedFor === opt}
                onChange={() => handleSubmittedForChange(opt)}
                className="accent-[var(--sidebar-bg)]"
              />
              {opt}
            </label>
          ))}
        </div>

        {form.submittedFor === "Family Member" && (
          <FormField label="Select Family Member">
            <MultiSelect
              options={familyOptions}
              value={form.familyMember}
              onChange={(v) => {
                const last = v.length ? v[v.length - 1] : null;
                if (last === "__new_family_member__") {
                  setIsNewFamilyOpen(true);
                  return;
                }
                setForm((f: any) => ({
                  ...f,
                  familyMember: last ? [last] : [],
                }));
              }}
              placeholder={familyLoading ? "Loading…" : "Select family member…"}
            />
          </FormField>
        )}

        {attendeeStatus !== "AWAITING" &&
          initialSubmittedFor !== "Family Member" && (
            <>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveProfileTab("user")}
                  className={
                    "rounded-md px-3 py-1 text-sm " +
                    (activeProfileTab === "user"
                      ? "bg-blue-600 text-white"
                      : "border border-neutral-200 text-neutral-700")
                  }
                >
                  User Profile
                </button>
                <button
                  type="button"
                  onClick={() => setActiveProfileTab("sport")}
                  className={
                    "rounded-md px-3 py-1 text-sm " +
                    (activeProfileTab === "sport"
                      ? "bg-blue-600 text-white"
                      : "border border-neutral-200 text-neutral-700")
                  }
                >
                  Sports Profile
                </button>
                <div className="ml-4">
                  <label className="sr-only">Profile language</label>
                  <select
                    value={selectedLocale}
                    onChange={(e) => setSelectedLocale(e.target.value)}
                    className="rounded-md border border-neutral-200 px-2 py-1 text-sm"
                  >
                    {(
                      (selectedProfiles && selectedProfiles.map((p: any) => p?.locale).filter(Boolean)) ||
                      ["en"]
                    ).filter((v: any, i: number, a: any[]) => a.indexOf(v) === i).map((loc: any) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                    {/* fallback common languages */}
                    {!selectedProfiles && ["en", "fr", "cn", "es"].map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
              </div>
              {activeProfileTab === "user" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="First Name" required>
                      <input
                        className={inputCls}
                          value={form.firstName}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, firstName: e.target.value }))
                          }
                          disabled
                        placeholder="first name"
                        required
                      />
                    </FormField>
                    <FormField label="Last Name" required>
                      <input
                        className={inputCls}
                          value={form.lastName}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, lastName: e.target.value }))
                          }
                          disabled
                        placeholder="last name"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Email" required>
                      <input
                        type="email"
                        className={inputCls}
                        value={form.email}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, email: e.target.value }))
                        }
                        disabled
                        placeholder="you@email.com"
                        required
                      />
                    </FormField>
                    <FormField label="Phone" required>
                      <input
                        type="tel"
                        className={inputCls}
                        value={form.phone}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, phone: e.target.value }))
                        }
                        disabled
                        placeholder="+44..."
                        required
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Date of Birth" required>
                      <input
                        type="date"
                        className={inputCls}
                        value={form.dateOfBirth}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            dateOfBirth: e.target.value,
                          }))
                        }
                        required
                      />
                    </FormField>
                      <FormField label="Gender">
                      <MultiSelect
                        options={GENDER.map((g) => ({ value: g, label: g }))}
                        value={form.gender}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            gender: v.length ? [v[v.length - 1]] : [],
                          }))
                        }
                        placeholder="Select gender…"
                      />
                    </FormField>

                    {/* <FormField label="Team Applying For">
                      <input
                        className={inputCls}
                        value={form.team}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, team: e.target.value }))
                        }
                      />
                    </FormField> */}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Ethnicity">
                      <MultiSelect
                        options={makeOptions("ethnicity")}
                        value={form.ethnicity}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            ethnicity: v.length ? [v[v.length - 1]] : [],
                          }))
                        }
                        placeholder="Select ethnicity…"
                      />
                    </FormField>
                    <FormField label="Height">
                      <MultiSelect
                        options={makeOptions("height_range")}
                        value={form.height}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            height: v.length ? [v[v.length - 1]] : [],
                          }))
                        }
                        placeholder="Select height…"
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Citizenship">
                      <MultiSelect
                        options={makeOptions("citizenship_multi_select")}
                        value={form.citizenship}
                        onChange={(v) => setForm((f) => ({ ...f, citizenship: v }))}
                        placeholder="Select citizenship(s)…"
                      />
                    </FormField>
                    <FormField label="TBC">
                      <MultiSelect
                        options={GENDER.map((g) => ({ value: g, label: g }))}
                        value={form.gender}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            gender: v.length ? [v[v.length - 1]] : [],
                          }))
                        }
                        placeholder="Select gender…"
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Medical">
                      <MultiSelect
                        options={makeOptions("chronic_conditions")}
                        value={form.medical}
                        onChange={(v) => setForm((f) => ({ ...f, medical: v }))}
                        placeholder="Select medical conditions…"
                      />
                    </FormField>
                    <FormField label="Allergies">
                      <MultiSelect
                        options={makeOptions("allergies")}
                        value={form.allergies}
                        onChange={(v) => setForm((f) => ({ ...f, allergies: v }))}
                        placeholder="Select allergies…"
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Address">
                      <input
                        className={inputCls}
                        value={form.address}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, address: e.target.value }))
                        }
                      />
                    </FormField>
                    <FormField label="City / Town">
                      <input
                        className={inputCls}
                        value={form.city}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, city: e.target.value }))
                        }
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Postal / Zip Code">
                      <input
                        className={inputCls}
                        value={form.postalCode}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, postalCode: e.target.value }))
                        }
                      />
                    </FormField>
                    <FormField label="Country">
                      <input
                        className={inputCls}
                        value={form.country}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, country: e.target.value }))
                        }
                      />
                    </FormField>
                  </div>

                  <FormField label="Notes (Optional)">
                    <textarea
                      className={inputCls + " resize-none"}
                      rows={3}
                      value={form.notes}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, notes: e.target.value }))
                      }
                      placeholder="Any additional information…"
                    />
                  </FormField>
                </>
              )}
              {activeProfileTab === "sport" && (
                <div>
                  <Section title="Sport Information" />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Sport">
                      <input
                        className={inputCls}
                        value={humanizeSport((inferredSport === "sports") ? (form.sportProfile.sport || inferredSport) : (inferredSport || form.sportProfile.sport)) || "Not specified"}
                        disabled
                        aria-readonly
                      />
                    </FormField>
                    <FormField label="Sport ID">
                      <input
                        className={inputCls}
                        value={form.sportProfile.sportId}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              sportId: e.target.value,
                            },
                          }))
                        }
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Current Club">
                      <input
                        className={inputCls}
                        value={form.sportProfile.currentClub}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              currentClub: e.target.value,
                            },
                          }))
                        }
                      />
                    </FormField>
                    <div />
                  </div>

                  <Section title="Positions" />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Preferred Position">
                      <MultiSelect
                        options={buildPositionOptions(
                          "preferred_positions_by_sport",
                          form.sportProfile.sport,
                        )}
                        value={form.sportProfile.preferredPosition}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              preferredPosition: v.length
                                ? [v[v.length - 1]]
                                : [],
                            },
                          }))
                        }
                        placeholder="Select preferred position…"
                      />
                    </FormField>
                    <FormField label="Secondary Position">
                      <MultiSelect
                        options={buildPositionOptions(
                          "secondary_positions_by_sport",
                          form.sportProfile.sport,
                        )}
                        value={form.sportProfile.secondaryPosition}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              secondaryPosition: v.length
                                ? [v[v.length - 1]]
                                : [],
                            },
                          }))
                        }
                        placeholder="Select secondary position…"
                      />
                    </FormField>
                  </div>

                  <Section title="Physical Attributes" />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Dominant Foot">
                      <MultiSelect
                        options={buildKeywordOptions("dominant_foot")}
                        value={form.sportProfile.dominantFoot}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              dominantFoot: v.length ? [v[v.length - 1]] : [],
                            },
                          }))
                        }
                        placeholder="Select dominant foot…"
                      />
                    </FormField>
                    <FormField label="Dominant Hand">
                      <MultiSelect
                        options={buildKeywordOptions("dominant_hand")}
                        value={form.sportProfile.dominantHand}
                        onChange={(v) =>
                          setForm((f: any) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              dominantHand: v.length ? [v[v.length - 1]] : [],
                            },
                          }))
                        }
                        placeholder="Select dominant hand…"
                      />
                    </FormField>
                  </div>

                  <Section title="Training & Performance" />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Training Frequency">
                      <MultiSelect
                        options={buildKeywordOptions("training_frequency")}
                        value={form.sportProfile.trainingFrequency}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              trainingFrequency: v,
                            },
                          }))
                        }
                        placeholder="Select training frequency…"
                      />
                    </FormField>
                    <FormField label="Preferred Training Style">
                      <MultiSelect
                        options={buildKeywordOptions(
                          "preferred_training_style",
                        )}
                        value={form.sportProfile.preferredTrainingStyle}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              preferredTrainingStyle: v,
                            },
                          }))
                        }
                        placeholder="Select training styles…"
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Past Injuries">
                      <MultiSelect
                        options={buildKeywordOptions("past_sports_injuries")}
                        value={form.sportProfile.pastInjuries}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            sportProfile: {
                              ...f.sportProfile,
                              pastInjuries: v,
                            },
                          }))
                        }
                        placeholder="Select past injuries…"
                      />
                    </FormField>
                    <div />
                  </div>
                </div>
              )}
            </>
          )}
        <Section title="Consent & Permissions" />
        <div className="space-y-2">
          {/* Enforced SPORTS keyword sharing for event creator company */}
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={true}
              disabled={isSportsEvent && isRequesterCompany}
              onChange={() => { /* intentionally noop - enforced by server when applicable */ }}
              className="mt-1 h-4 w-4 accent-[var(--sidebar-bg)]"
            />
            <span className="text-sm">
              Sports keywords are required to be shared for this event.
              <span
                className="ml-2 text-xs text-neutral-500"
                title="Sports data are required to be shared for this event and cannot be turned off."
              >
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={!!form.termsAccepted}
              onChange={(e) =>
                setForm((f: any) => ({ ...f, termsAccepted: e.target.checked }))
              }
              className="mt-1 h-4 w-4 accent-[var(--sidebar-bg)]"
            />
            <span className="text-sm">
              I accept Terms and conditions andEvent Policies.            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={!!form.videoConsent}
              onChange={(e) =>
                setForm((f: any) => ({ ...f, videoConsent: e.target.checked }))
              }
              className="mt-1 h-4 w-4 accent-[var(--sidebar-bg)]"
            />
            <span className="text-sm">
              I consent to video recording during training sessions and matches
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={!!form.socialMediaConsent}
              onChange={(e) =>
                setForm((f: any) => ({
                  ...f,
                  socialMediaConsent: e.target.checked,
                }))
              }
              className="mt-1 h-4 w-4 accent-[var(--sidebar-bg)]"
            />
            <span className="text-sm">
              I consent to the use of photos/videos for social media and
              promotional purposes
            </span>
          </label>
        </div>
      </form>
    </AppModal>
  );
}
